import { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGovernanceStore } from './useGovernanceStore';
import { ApprovalBadge } from './ApprovalBadge';
import { ApprovalWorkflow } from './ApprovalWorkflow';
import { ApprovalTimeline } from './ApprovalTimeline';
import { useApiToken } from '@/auth/hooks';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';
import type { ApprovalStatus } from './types';
import { toast } from 'sonner';

const STATUS_TABS: { label: string; value: ApprovalStatus | null }[] = [
  { label: 'Alle', value: null },
  { label: 'Offen', value: 'InReview' },
  { label: 'Genehmigt', value: 'Approved' },
  { label: 'Abgelehnt', value: 'Rejected' },
  { label: 'Live', value: 'Live' },
];

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function ApprovalsTab() {
  const { getAccessToken } = useApiToken();
  const userId = useOrgMemberStore((s) => s.currentUser?.userId ?? '');
  const store = useGovernanceStore();

  useEffect(() => {
    void store.loadApprovals(getAccessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.approvalsPage, store.approvalsStatusFilter]);

  const handleApprove = async (id: string) => {
    try {
      await store.approveApproval(id, getAccessToken);
      toast.success('Genehmigung erteilt');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler beim Genehmigen');
    }
  };

  const handleReject = async (id: string, note: string | undefined) => {
    try {
      await store.rejectApproval(id, note, getAccessToken);
      toast.success('Genehmigung abgelehnt');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler beim Ablehnen');
    }
  };

  const handleGoLive = async (id: string) => {
    try {
      await store.goLiveApproval(id, getAccessToken);
      toast.success('Live geschaltet');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  const handleResubmit = async (id: string) => {
    try {
      await store.resubmitApproval(id, getAccessToken);
      toast.success('Erneut eingereicht');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler');
    }
  };

  return (
    <div className="space-y-4">
      {/* Status-Filter Tabs */}
      <div className="flex gap-1">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={store.approvalsStatusFilter === tab.value ? 'default' : 'ghost'}
            size="sm"
            onClick={() => store.setApprovalsStatusFilter(tab.value)}
          >
            {tab.label}
            {tab.value === 'InReview' && store.approvalsTotalCount > 0 && (
              <span className="ml-1 text-xs">({store.approvals.filter(a => a.status === 'InReview').length})</span>
            )}
          </Button>
        ))}
      </div>

      {/* Loading */}
      {store.approvalsLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty State */}
      {!store.approvalsLoading && store.approvals.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Keine Genehmigungen vorhanden.
        </div>
      )}

      {/* Approval Cards */}
      {!store.approvalsLoading && store.approvals.map((approval) => (
        <Card key={approval.id}>
          <CardContent className="flex flex-col gap-3 pt-4 pb-4 md:flex-row md:items-start md:justify-between">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <ApprovalBadge status={approval.status} />
                <span className="text-sm font-medium">{approval.targetLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Von: {approval.submitterDisplayName ?? approval.submitterId} · Eingereicht: {formatDateTime(approval.createdAt)}
              </p>
              <ApprovalTimeline approval={approval} />
            </div>
            <div className="shrink-0">
              <ApprovalWorkflow
                approval={approval}
                currentUserId={userId ?? ''}
                onApprove={handleApprove}
                onReject={handleReject}
                onGoLive={handleGoLive}
                onResubmit={handleResubmit}
              />
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Pagination */}
      {store.approvalsTotalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted-foreground">
            Seite {store.approvalsPage} von {store.approvalsTotalPages}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              disabled={store.approvalsPage <= 1}
              onClick={() => store.setApprovalsPage(store.approvalsPage - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={store.approvalsPage >= store.approvalsTotalPages}
              onClick={() => store.setApprovalsPage(store.approvalsPage + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
